"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import NextImage from "next/image";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { PageTradingCompetitionSchema } from "@/sanity/pages/PageTradingCompetitionTemplate";
import CtaHover from "./CtaHover";
import SelectionHighlight, { type SelectionGeometry } from "./SelectionHighlight";
import { useCtaHref } from "./ctaHref";
import { BreachWidget, MultiplierWidget, PointsWidget, useCycle } from "./rules/RuleWidgets";
import { at, useReveal } from "./motion";

/** A card is its own reveal scope: it rises in turn and tells the title which number to light. Hovering it replays its widget. */
function RevealCard({
  index,
  onLive,
  onHover,
  className,
  children,
}: {
  index: number;
  onLive: (index: number) => void;
  onHover?: () => void;
  className: string;
  children: React.ReactNode;
}) {
  const [ref, reveal] = useReveal<HTMLElement>({ threshold: 0.3 });
  useEffect(() => {
    if (reveal === "live") onLive(index);
  }, [reveal, index, onLive]);
  return (
    <article
      ref={ref}
      data-reveal={reveal}
      style={at(index * 0.15, 1)}
      onMouseEnter={onHover}
      className={cn("tc-rv-rise", className)}>
      {children}
    </article>
  );
}

/** "1, 2, 3." selection: Figma 89×37 at 34px (mobile), 146×59 at 48px (desktop), measured against the glyphs. */
const TITLE_SELECTION = {
  mobile: {
    font: 34,
    box: [-6.06, -5.07, 89, 37],
    word: 77.44,
    handles: [[-0.75, 0, 1.5, 33], [-2, 33, 4, 4], [87, 0, 4, 4], [88.25, 4, 1.5, 33]],
  },
  desktop: {
    font: 48,
    box: [-15.58, -6, 146, 59],
    word: 115.13,
    handles: [[0, 0, 2, 59], [-2, 53, 6, 6], [142, 0, 6, 6], [144, 0, 2, 59]],
  },
} satisfies Record<string, SelectionGeometry>;

/** The shadow under the glass around each widget. */
const GLASS = [
  "shadow-[0_3rem_5rem_-2.5rem_rgba(0,0,0,0.55)]",
  "drop-shadow-[0_3rem_2.5rem_rgba(0,0,0,0.55)]",
  "shadow-[0_3rem_5rem_-2.5rem_rgba(0,0,0,0.55)]",
];

/** Desktop decoration: the IQ texture twice, rotated, under a radial fade (Figma "Group 2085665573"). */
function TopTexture() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 left-1/2 hidden h-[48rem] w-[240.25rem] -translate-x-1/2 overflow-hidden lg:block"
      style={{
        maskImage: "radial-gradient(125.79rem 48rem at 120.125rem 0, #000 0%, transparent 100%)",
        WebkitMaskImage: "radial-gradient(125.79rem 48rem at 120.125rem 0, #000 0%, transparent 100%)",
      }}>
      {[25.5, 120].map((left, i) => (
        <div key={left} className="absolute top-[-26.5rem] flex h-[97.375rem] w-[94.625rem] items-center justify-center" style={{ left: `${left}rem` }}>
          {/* Figma blends it with "exclusion" over white, which is the inverted image */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/trading-competition/rules-texture.jpg"
            alt=""
            className="tc-anim tc-drift h-[94.625rem] w-[97.375rem] max-w-none rotate-90 opacity-30 invert"
            style={{ animationDelay: `${-i * 7}s` }}
          />
        </div>
      ))}
    </div>
  );
}

export default function TradingCompetitionRules({ data }: { data: PageTradingCompetitionSchema }) {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const running = visible && !reduced;

  // "1, 2, 3.": each number lights as its card arrives. null = static (all lit), until JS runs.
  const [headerRef, headerReveal] = useReveal();
  const [ctaRef, ctaReveal] = useReveal<HTMLAnchorElement>({ threshold: 0.5 });
  const cardCount = data.rulesCards?.length ?? 0;
  const [lit, setLit] = useState<boolean[] | null>(null);
  useEffect(() => {
    if (!reduced && cardCount) setLit(Array.from({ length: cardCount }, () => false));
  }, [reduced, cardCount]);
  const onCardLive = useCallback((i: number) => setLit((prev) => prev && prev.map((v, j) => v || j === i)), []);

  // each widget replays on its own beat while the section is on screen
  const [pointsCycle, replayPoints] = useCycle(running, 8000);
  const [breachCycle, replayBreach] = useCycle(running, 8500);
  const [multiplierCycle, replayMultiplier] = useCycle(running, 7000);
  const replays = [replayPoints, replayBreach, replayMultiplier];

  const cards = data.rulesCards ?? [];
  if (!cards.length) return null;
  const widgets = [
    <PointsWidget key="points" data={data} cycle={pointsCycle} />,
    <BreachWidget key="breach" data={data} cycle={breachCycle} />,
    <MultiplierWidget key="multiplier" data={data} cycle={multiplierCycle} />,
  ];
  const ctaHref = useCtaHref(data.rulesCta, data.settings?.language);

  return (
    <section
      ref={ref}
      id="rules"
      data-paused={running ? undefined : ""}
      className="relative flex scroll-mt-[7.5rem] flex-col items-center gap-[4rem] overflow-hidden bg-white px-[2rem] pt-[7rem] pb-[8rem] lg:gap-[8rem] lg:px-0 lg:py-[12.5rem]">
      <TopTexture />

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div ref={headerRef} data-reveal={headerReveal} className="relative flex w-full flex-col items-center gap-[1.75rem] lg:w-auto lg:gap-[2rem]">
        {data.rulesEyebrow && (
          <p style={at(0)} className="tc-rv-rise mb-0 font-blacker text-[2.125rem] leading-[0.9444] font-normal whitespace-nowrap text-[rgba(0,27,20,0.7)] italic lg:text-[2rem] lg:leading-[1.5]">
            {data.rulesEyebrow}
          </p>
        )}
        <h2 style={at(0.12, 1)} className="tc-rv-rise mb-0 translate-x-[0.375rem] text-center font-geist text-[4.25rem] leading-[1.04] font-medium tracking-[-0.1913rem] whitespace-pre text-[#001b14] lg:translate-x-0 lg:text-[6rem] lg:leading-[1.15] lg:tracking-[-0.3rem]">
          {data.rulesTitleBefore}
          {/* Figma's own spacing before the highlight: two spaces on mobile, three on desktop */}
          <span className="lg:hidden">{"  "}</span>
          <span className="hidden lg:inline">{"   "}</span>
          <span className="relative font-blacker leading-[0] font-normal text-[#228e71] italic lg:tracking-[-0.18rem] lg:text-[#003828]">
            <SelectionHighlight
              {...TITLE_SELECTION}
              fill="rgba(34,142,113,0.12)"
              mobileFill="rgba(34,142,113,0.1)"
              handle="#228e71"
              hatch="/trading-competition/highlight-hatch-bonus.png"
              delay={0.85}
            />
            <span className="relative font-blacker">
              {(data.rulesTitleHighlight ?? "").split(" ").map((token, i, all) => (
                <span
                  key={i}
                  className="font-blacker transition-opacity duration-700 ease-out"
                  style={{ opacity: !lit || lit[i] ? 1 : 0.2, transitionDelay: "0.45s" }}>
                  {token}
                  {i < all.length - 1 ? " " : ""}
                </span>
              ))}
            </span>
          </span>
        </h2>
      </div>

      {/* ── Cards ──────────────────────────────────────────────────────── */}
      <div className="relative flex w-full flex-col gap-[2rem] lg:w-[150rem] lg:flex-row lg:items-stretch">
        {cards.map((card, i) => (
          <RevealCard
            key={card._key ?? i}
            index={i}
            onLive={onCardLive}
            onHover={replays[i]}
            className="flex min-w-0 flex-col items-start overflow-hidden rounded-[0.5rem] border border-solid border-[rgba(0,27,20,0.1)] bg-white px-[2rem] pt-[2rem] lg:flex-1 lg:basis-0">
            <div
              className={cn(
                "relative isolate h-[35.25rem] w-full shrink-0 overflow-hidden bg-[#131b1b]",
                i === 0 ? "rounded-[0.75rem]" : "rounded-[0.25rem]",
              )}>
              {card.image && (
                <NextImage
                  src={card.image.src}
                  alt=""
                  fill
                  sizes="(max-width: 992px) 100vw, 25vw"
                  quality={90}
                  className="tc-anim tc-kenburns -z-10 object-cover"
                  style={{ animationDelay: `${-i * 4}s` }}
                />
              )}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                {/* 12px of glass on every side (Figma: 8×4 on the first and last, 4 on the middle; widened on review) */}
                <div className={cn("relative flex flex-col items-start rounded-[1.25rem] border border-solid border-white/[0.12] p-[1.5rem]", GLASS[i % 3])}>
                  <i aria-hidden="true" className="absolute inset-0 rounded-[1.25rem] bg-white/10 backdrop-blur-[0.875rem]" />
                  {widgets[i]}
                  <i aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0_0.125rem_0_0_rgba(255,255,255,0.18)]" />
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-start gap-[1rem] pt-[3rem] pb-[3.5rem] lg:px-[3rem]">
              <h3 className="mb-0 font-geist text-[2.5rem] leading-[normal] font-medium tracking-[-0.05rem] whitespace-nowrap text-[#001b14]">
                {card.title}
              </h3>
              <p className="mb-0 font-geist text-[2.25rem] leading-[1.5] font-normal whitespace-pre-line text-[rgba(0,27,20,0.66)]">
                {card.description}
              </p>
            </div>
          </RevealCard>
        ))}
      </div>

      {data.rulesCta?.text && (
        <a
          ref={ctaRef}
          data-reveal={ctaReveal}
          href={ctaHref}
          style={at(0.1)}
          className="tc-rv-rise group relative flex h-[6rem] w-full items-center justify-center overflow-hidden rounded-[0.25rem] border border-solid border-black/[0.12] px-[3rem] lg:h-[5.875rem] lg:w-auto">
          <CtaHover
            label={data.rulesCta.text}
            fills={["bg-black", "bg-[#307f6a]"]}
            labelClassName="font-geist text-[1.75rem] leading-[1.347] font-semibold tracking-[-0.035rem] whitespace-nowrap text-white"
          />
        </a>
      )}

      <style jsx global>{`
        @keyframes tc-grow {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @keyframes tc-pop {
          from { transform: translateY(35%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes tc-kenburns {
          from { transform: scale(1) translate(0, 0); }
          to { transform: scale(1.08) translate(-1.5%, -1%); }
        }
        @keyframes tc-drift {
          from { transform: rotate(90deg) scale(1) translate(0, 0); }
          to { transform: rotate(90deg) scale(1.06) translate(2%, -1.5%); }
        }
        .tc-pop { animation: tc-pop 450ms cubic-bezier(0.22, 0.61, 0.36, 1) both; }
        .tc-kenburns { animation: tc-kenburns 16s ease-in-out infinite alternate; }
        .tc-drift { animation: tc-drift 22s ease-in-out infinite alternate; }
        [data-paused] .tc-anim { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .tc-anim, .tc-pop { animation: none; }
        }
      `}</style>
    </section>
  );
}
